/**
 * WHAT THE CLIENT CHECKS BEFORE IT BELIEVES A CONFIG DOCUMENT.
 *
 * ADR-0004 makes the bundled card table a cache of this document. Adopting a
 * document means the next match is played on its stats — so the client refuses
 * anything it cannot fully account for, and keeps what it already had. The
 * failure mode of refusing wrongly is "you play on the version you already
 * agreed with"; the failure mode of accepting wrongly is a desync at tick 1.
 *
 * The checks, and why each one is here rather than assumed:
 *
 *  1. SHAPE AND FORMAT. A future format is not a document this build can read.
 *     Refusing is honest; guessing which fields still mean what is not.
 *  2. CHECKSUM. Recomputed over the canonical form of the payload actually
 *     received and compared with the number the server computed over the
 *     payload it sent. This is what catches a truncated response, a proxy that
 *     rewrote something, and a corrupted `localStorage` entry.
 *  3. INTEGER-ONLY STATS. `canonicalJson` throws on a float, so the checksum
 *     step doubles as ADR-0003's law: a non-integer stat cannot be adopted.
 *  4. ASCENDING, UNIQUE CARD IDS. The sim resolves a deck to TABLE INDICES. Two
 *     peers whose tables are ordered differently agree about every stat and
 *     disagree about every card, which is the worst possible desync because
 *     nothing looks wrong until a unit appears on the wrong side of the board.
 *  5. MONOTONIC REVISION. A document older than the one already adopted is a
 *     stale cache answering after a fresh one — accepting it would silently
 *     undo a rollback the operator just performed.
 */
import { CanonicalJsonError, canonicalJson, checksumOf } from './config-codec';
import { CONFIG_DOC_KIND, CONFIG_FORMAT, type PublicFlag } from './config.types';

export interface ValidationOutcome {
  readonly ok: boolean;
  /** A short, specific reason. Logged and surfaced, never swallowed. */
  readonly reason: string | null;
}

const bad = (reason: string): ValidationOutcome => ({ ok: false, reason });
const good: ValidationOutcome = { ok: true, reason: null };

/**
 * Validate a fetched or cached envelope.
 *
 * `currentRevision` is the revision already adopted; pass `-1` to skip the
 * monotonicity check (reading the cache at boot, where there is nothing to be
 * newer than).
 */
export function validateEnvelope(value: unknown, currentRevision: number): ValidationOutcome {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return bad('the config document is not an object');
  }
  // Read through an index signature rather than a `Partial<RemoteConfigEnvelope>`
  // cast: this function's whole job is to establish that the value HAS that
  // shape, and starting from a type that asserts it would let a missing field
  // typecheck its way past the very check meant to catch it.
  const env = value as Record<string, unknown>;

  const revision = env['revision'];
  const cardTableVersion = env['cardTableVersion'];
  const checksum = env['checksum'];

  if (env['kind'] !== CONFIG_DOC_KIND) return bad(`unexpected document kind ${JSON.stringify(env['kind'])}`);
  if (!Number.isInteger(revision) || (revision as number) < 0) return bad('revision is not a whole number');
  if (!Number.isInteger(cardTableVersion) || (cardTableVersion as number) < 1) {
    return bad('cardTableVersion is not a whole number >= 1');
  }
  if (typeof checksum !== 'string' || !/^[0-9a-f]{8}$/.test(checksum)) {
    return bad('checksum is not eight hex characters');
  }
  if (currentRevision >= 0 && (revision as number) < currentRevision) {
    return bad(`revision ${String(revision)} is older than the one already in use (${currentRevision})`);
  }

  const payload = env['payload'];
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return bad('the document has no payload object');
  }
  const p = payload as Record<string, unknown>;
  if (p['format'] !== CONFIG_FORMAT) {
    return bad(`document format ${String(p['format'])}, this build reads ${CONFIG_FORMAT}`);
  }
  if (p['cardTableVersion'] !== cardTableVersion) {
    return bad('the envelope and the payload disagree about cardTableVersion');
  }

  const cards = p['cards'];
  if (!Array.isArray(cards) || cards.length === 0) return bad('the document carries no cards');

  let previous = '';
  for (const card of cards) {
    if (card === null || typeof card !== 'object' || Array.isArray(card)) return bad('a card is not an object');
    const id: unknown = (card as { id?: unknown }).id;
    if (typeof id !== 'string' || id.length === 0) return bad('a card has no id');
    if (previous !== '' && id <= previous) {
      return bad(`cards are not in ascending id order (${previous} then ${id})`);
    }
    previous = id;
  }

  const flags = p['flags'];
  if (!Array.isArray(flags)) return bad('the document carries no flag list');
  const flagReason = checkFlags(flags);
  if (flagReason !== null) return bad(flagReason);

  // Last, because it is the only step that can throw and because everything
  // above narrows what a mismatch could mean.
  let canonical: string;
  try {
    canonical = canonicalJson(payload);
  } catch (error) {
    return bad(
      error instanceof CanonicalJsonError
        ? `the payload is not canonicalisable — ${error.message}`
        : 'the payload is not canonicalisable',
    );
  }
  const computed = checksumOf(canonical);
  if (computed !== checksum) {
    return bad(`checksum mismatch (document says ${checksum}, the bytes are ${computed})`);
  }
  return good;
}

function checkFlags(flags: readonly unknown[]): string | null {
  const seen = new Set<string>();
  for (const flag of flags) {
    if (flag === null || typeof flag !== 'object' || Array.isArray(flag)) return 'a flag is not an object';
    const f = flag as Partial<PublicFlag> & Record<string, unknown>;
    if (typeof f.key !== 'string' || f.key === '') return 'a flag has no key';
    if (seen.has(f.key)) return `duplicate flag key ${f.key}`;
    seen.add(f.key);
    if (typeof f.enabled !== 'boolean') return `${f.key}: enabled is not a boolean`;
    if (!Number.isInteger(f.rollout) || (f.rollout as number) < 0 || (f.rollout as number) > 100) {
      return `${f.key}: rollout is not an integer 0..100`;
    }
  }
  return null;
}
